/**
 * Unified AI service that routes to the configured provider (Gemini or ChatGPT)
 */

import { geminiService, ConsultationContext as GeminiContext } from './geminiService'
import { chatgptService, ConsultationContext as ChatGPTContext } from './chatgptService'
import type { MedicationOrderContext, MedicationOrderInsight } from '../types/medicationAI'
import { api } from './api'

export type AIProvider = 'gemini' | 'chatgpt'
export type GeminiModel = 'gemini-2.5-flash' | 'gemini-2.5-pro' | 'gemini-2.0-flash' | 'gemini-2.0-flash-001'
export type ChatGPTModel = 'gpt-4o' | 'gpt-4o-mini' | 'gpt-4-turbo' | 'gpt-4' | 'gpt-3.5-turbo'

export interface AIConfig {
  provider: AIProvider
  geminiModel?: GeminiModel
  chatgptModel?: ChatGPTModel
  chatgptApiKey?: string
}

export interface AISuggestion {
  type: 'diagnosis' | 'treatment' | 'test' | 'referral' | 'medication' | 'followup'
  title: string
  description: string
  confidence?: 'high' | 'medium' | 'low'
}

export interface ConsultationContext {
  patientAge?: number
  patientGender?: string
  reasonForVisit?: string
  notes?: string
  currentMedications?: string[]
  allergies?: string[]
  chronicConditions?: string[]
  medicalHistory?: string
  diagnosis?: string[]
  vitalSigns?: Record<string, any>
}

// Default configuration
const DEFAULT_CONFIG: AIConfig = {
  provider: 'gemini',
  geminiModel: 'gemini-2.5-flash',
  chatgptModel: 'gpt-4o-mini',
}

// Get configuration from localStorage
function getConfig(): AIConfig {
  try {
    const saved = localStorage.getItem('clnicl_ai_config')
    if (saved) {
      return { ...DEFAULT_CONFIG, ...JSON.parse(saved) }
    }
  } catch (error) {
    console.error('[AIService] Error loading config:', error)
  }
  return DEFAULT_CONFIG
}

// Save configuration to localStorage
export function saveAIConfig(config: AIConfig): void {
  try {
    localStorage.setItem('clnicl_ai_config', JSON.stringify(config))
  } catch (error) {
    console.error('[AIService] Error saving config:', error)
  }
}

// Get current configuration
export function getAIConfig(): AIConfig {
  return getConfig()
}

// Get current provider and model info for display
export function getCurrentModelInfo(): { provider: AIProvider; model: string } {
  const config = getConfig()
  return {
    provider: config.provider,
    model: config.provider === 'gemini' ? (config.geminiModel || 'gemini-2.5-flash') : (config.chatgptModel || 'gpt-4o-mini'),
  }
}

export const aiService = {
  /**
   * Get AI suggestions using the configured provider
   */
  getSuggestions: async (context: ConsultationContext): Promise<AISuggestion[]> => {
    const config = getConfig()

    if (config.provider === 'gemini') {
      const suggestions = await geminiService.getSuggestions(context as GeminiContext)
      return suggestions as AISuggestion[]
    } else if (config.provider === 'chatgpt') {
      if (!config.chatgptApiKey) {
        throw new Error('ChatGPT API key is required. Please configure it in Settings > AI Configuration.')
      }
      const model = config.chatgptModel || 'gpt-4o-mini'
      const suggestions = await chatgptService.getSuggestions(context as ChatGPTContext, config.chatgptApiKey, model)
      return suggestions as AISuggestion[]
    } else {
      throw new Error(`Unknown AI provider: ${config.provider}`)
    }
  },

  /**
   * Adjunct medication-order insights (explanations + formulary-ranked alternatives).
   * When VITE_AI_ASSIST_PROXY is "true", calls POST /ai-assist/medication-order-insights (server-side key).
   * Otherwise uses the configured client-side provider (Gemini or ChatGPT).
   */
  getMedicationOrderInsights: async (context: MedicationOrderContext): Promise<MedicationOrderInsight> => {
    const useProxy = import.meta.env.VITE_AI_ASSIST_PROXY === 'true'
    if (useProxy) {
      const response = await api.post<MedicationOrderInsight>('/ai-assist/medication-order-insights', {
        context,
      })
      return response.data
    }

    const config = getConfig()
    if (config.provider === 'gemini') {
      return geminiService.getMedicationOrderInsights(context)
    }
    if (config.provider === 'chatgpt') {
      if (!config.chatgptApiKey) {
        throw new Error('ChatGPT API key is required. Please configure it in Settings > AI Configuration.')
      }
      const model = config.chatgptModel || 'gpt-4o-mini'
      return chatgptService.getMedicationOrderInsights(context, config.chatgptApiKey, model)
    }
    throw new Error(`Unknown AI provider: ${config.provider}`)
  },
}






























